/**
 * Required-field validation helpers for the ServiceFlowConfigurator.
 * Operates on the `context.fields` array populated by fieldFactory.js
 * (each field carries `.inputs`, `.errorElement` and `.IsRequired`).
 */
import { setFieldError } from './fieldFactory';

// Returns true when a field's input(s) are empty. Fields that need custom
// "is empty" logic (e.g. multiselect, file upload) can override this via
// field.isSelectionEmptyOverride (see fieldFactory.js).
function isFieldEmpty(field) {
  if (typeof field.isSelectionEmptyOverride === 'function') {
    return field.isSelectionEmptyOverride();
  }

  const inputs = field.inputs || [];
  if (inputs.length === 0) {
    return false;
  }

  return inputs.some((input) => {
    if (!input) {
      return true;
    }
    if (input.type === 'checkbox') {
      return !input.checked;
    }
    return !input.value || !input.value.trim();
  });
}

// Validates a single field, showing/clearing its "mandatory" error message.
// Returns true when the field is valid (or not required).
function revalidateField(field, translations) {
  if (!field.IsRequired || !field.errorElement) {
    return true;
  }

  const requiredFieldMessage =
    field.customRequiredMessage ||
    (translations && translations.thisStepIsMandatory) ||
    'This field is mandatory';

  if (isFieldEmpty(field)) {
    setFieldError(field, requiredFieldMessage);
    return false;
  }

  setFieldError(field, '');
  return true;
}

export function validateAllFields(fields, translations) {
  let isValid = true;

  (fields || []).forEach((field) => {
    if (!revalidateField(field, translations)) {
      isValid = false;
    }
  });

  return isValid;
}

// After the first submit attempt,
// focus, instead of waiting for the next full validate() call.
export function attachLiveRevalidation(getFields, container, translations, isSubmitted) {
  function handleFieldBlur(event) {
    const submitted = typeof isSubmitted === 'function' ? isSubmitted() : isSubmitted;
    if (!submitted) {
      return;
    }

    const target = event.target;
    const fields = typeof getFields === 'function' ? getFields() : getFields;
    const matchingField = (fields || []).find((field) => (field.inputs || []).includes(target));

    if (!matchingField) {
      return;
    }

    revalidateField(matchingField, translations);
  }
  container.addEventListener('blur', handleFieldBlur, true);
}

// Shows backend (submitorder) errors on the matching fields by FieldId.
// Returns the errors that could not be matched to a rendered field.
export function applyServerErrors(fields, errors) {
  const normalizeId = (id) =>
    String(id || '')
      .replace(/[{}]/g, '')
      .toLowerCase();
  const unmatched = [];

  (errors || []).forEach((error) => {
    const field = (fields || []).find(
      (f) => normalizeId(f.Id) && normalizeId(f.Id) === normalizeId(error?.FieldId),
    );

    if (!field || !field.errorElement) {
      unmatched.push(error);
      return;
    }

    const message = String(error?.ErrorMessage || '').replace(/^\{|\}$/g, '');
    setFieldError(field, message || error?.ErrorType || 'Something went wrong');
  });

  return unmatched;
}
